import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { 
  Building2, 
  Download, 
  RefreshCw, 
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Clock,
  FileText,
  Users,
  Target,
  Zap,
  AlertCircle,
  ThumbsUp
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import * as XLSX from 'xlsx';
import './PanelCargaTrabajo.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PanelCargaTrabajo = () => {
  const { user, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [gestiones, setGestiones] = useState([]);
  const [instituciones, setInstituciones] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [gestionesRes, institucionesRes] = await Promise.all([
        axios.get(`${API}/gestiones?limit=5000`, { headers }),
        axios.get(`${API}/catalogos/instituciones`, { headers }).catch(() => ({ data: [] }))
      ]);

      setGestiones(gestionesRes.data.gestiones || gestionesRes.data || []);
      setInstituciones(institucionesRes.data || []);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  // Análisis de carga por institución
  const analisisCarga = useMemo(() => {
    const mapa = {};
    
    // Inicializar con todas las instituciones del catálogo
    instituciones.forEach(inst => {
      const nombre = inst.nombre || inst;
      if (!mapa[nombre]) {
        mapa[nombre] = {
          nombre,
          activos: 0,
          completados: 0,
          total: 0,
          enProceso: 0
        };
      }
    });
    
    // Agregar datos de gestiones
    gestiones.forEach(g => {
      const inst = g.institucion || 'Sin institución';
      
      if (!mapa[inst]) {
        mapa[inst] = {
          nombre: inst,
          activos: 0,
          completados: 0,
          total: 0,
          enProceso: 0
        };
      }
      
      mapa[inst].total++;
      
      if (g.estado === 'Completada / Archivada') {
        mapa[inst].completados++;
      } else {
        mapa[inst].activos++;
        if (g.estado === 'En proceso de análisis' || g.estado === 'En Revisión') {
          mapa[inst].enProceso++;
        }
      }
    });
    
    // Convertir a array y calcular nivel de carga
    const resultado = Object.values(mapa).map(inst => {
      let nivelCarga = 'bajo';
      let recomendacion = 'disponible';
      let alerta = null;
      
      if (inst.activos === 0) {
        nivelCarga = 'libre';
        recomendacion = 'recomendado';
      } else if (inst.activos === 1) {
        nivelCarga = 'bajo';
        recomendacion = 'disponible';
      } else if (inst.activos >= 2 && inst.activos <= 3) {
        nivelCarga = 'medio';
        recomendacion = 'moderado';
      } else if (inst.activos >= 4 && inst.activos <= 5) {
        nivelCarga = 'alto';
        recomendacion = 'precaución';
        alerta = 'Carga alta de casos';
      } else {
        nivelCarga = 'saturado';
        recomendacion = 'evitar';
        alerta = 'Saturación detectada';
      }
      
      return {
        ...inst,
        nivelCarga,
        recomendacion,
        alerta,
        porcentajeCompletado: inst.total > 0 ? Math.round((inst.completados / inst.total) * 100) : 0
      };
    });
    
    // Ordenar por activos (menor a mayor para recomendaciones)
    return resultado.sort((a, b) => a.activos - b.activos);
  }, [gestiones, instituciones]);

  // Estadísticas generales
  const estadisticas = useMemo(() => {
    const sinCarga = analisisCarga.filter(i => i.activos === 0).length;
    const bajaCarga = analisisCarga.filter(i => i.activos === 1).length;
    const mediaCarga = analisisCarga.filter(i => i.activos >= 2 && i.activos <= 3).length;
    const altaCarga = analisisCarga.filter(i => i.activos >= 4 && i.activos <= 5).length;
    const saturadas = analisisCarga.filter(i => i.activos > 5).length;
    
    const totalActivos = analisisCarga.reduce((acc, i) => acc + i.activos, 0);
    const totalCompletados = analisisCarga.reduce((acc, i) => acc + i.completados, 0);
    
    return {
      totalInstituciones: analisisCarga.length,
      sinCarga,
      bajaCarga,
      mediaCarga,
      altaCarga,
      saturadas,
      totalActivos,
      totalCompletados,
      promedioCarga: analisisCarga.length > 0 ? (totalActivos / analisisCarga.length).toFixed(1) : 0
    };
  }, [analisisCarga]);

  // Instituciones recomendadas (sin carga o baja carga)
  const recomendadas = useMemo(() => {
    return analisisCarga.filter(i => i.activos <= 2).slice(0, 10);
  }, [analisisCarga]);

  // Instituciones con alertas (alta carga o saturadas)
  const conAlertas = useMemo(() => {
    return analisisCarga.filter(i => i.alerta).sort((a, b) => b.activos - a.activos);
  }, [analisisCarga]);

  // Exportar a Excel
  const handleExportarExcel = () => {
    const datos = analisisCarga.map(inst => ({
      'Institución': inst.nombre,
      'Casos Activos': inst.activos,
      'Casos Completados': inst.completados,
      'Total Casos': inst.total,
      '% Completado': `${inst.porcentajeCompletado}%`,
      'Nivel de Carga': inst.nivelCarga.toUpperCase(),
      'Recomendación': inst.recomendacion.toUpperCase(),
      'Alerta': inst.alerta || '-'
    }));
    
    const ws = XLSX.utils.json_to_sheet(datos);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Carga Trabajo');
    XLSX.writeFile(wb, `Analisis_Carga_Trabajo_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Excel exportado correctamente');
  };

  // Obtener color según nivel de carga
  const getColorCarga = (nivel) => {
    switch (nivel) {
      case 'libre': return '#4CAF50';
      case 'bajo': return '#8BC34A';
      case 'medio': return '#FFC107';
      case 'alto': return '#FF9800';
      case 'saturado': return '#F44336';
      default: return '#666';
    }
  };

  // Obtener icono según recomendación
  const getIconoRecomendacion = (rec) => {
    switch (rec) {
      case 'recomendado': return <ThumbsUp size={16} />;
      case 'disponible': return <CheckCircle size={16} />;
      case 'moderado': return <AlertCircle size={16} />;
      case 'precaución': return <AlertTriangle size={16} />;
      case 'evitar': return <AlertTriangle size={16} />;
      default: return null;
    }
  };

  if (!isAdmin()) {
    return (
      <div className="panel-no-acceso">
        <AlertTriangle size={48} />
        <h2>Acceso Restringido</h2>
        <p>Esta funcionalidad está disponible únicamente para administradores.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="consulta-loading">
        <div className="loader"></div>
        <p>Analizando carga de trabajo...</p>
      </div>
    );
  }

  return (
    <div className="panel-carga-container" data-testid="panel-carga-trabajo">
      <div className="panel-header">
        <div>
          <h1>
            <Target size={28} />
            Panel de Carga de Trabajo
          </h1>
          <p>Análisis para toma de decisiones en asignación de casos</p>
        </div>
        <div className="panel-actions">
          <button className="btn btn-secondary" onClick={fetchData} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'spin' : ''} />
            Actualizar
          </button>
          <button className="btn btn-primary" onClick={handleExportarExcel}>
            <Download size={16} />
            Exportar Excel
          </button>
        </div>
      </div>

      {/* Resumen Ejecutivo */}
      <div className="resumen-ejecutivo">
        <div className="resumen-card total">
          <Building2 size={24} />
          <div>
            <span className="numero">{estadisticas.totalInstituciones}</span>
            <span className="label">Instituciones</span>
          </div>
        </div>
        <div className="resumen-card activos">
          <Clock size={24} />
          <div>
            <span className="numero">{estadisticas.totalActivos}</span>
            <span className="label">Casos Activos</span>
          </div>
        </div>
        <div className="resumen-card libres">
          <ThumbsUp size={24} />
          <div>
            <span className="numero">{estadisticas.sinCarga}</span>
            <span className="label">Sin Carga</span>
          </div>
        </div>
        <div className="resumen-card alertas">
          <AlertTriangle size={24} />
          <div>
            <span className="numero">{estadisticas.saturadas}</span>
            <span className="label">Saturadas</span>
          </div>
        </div>
        <div className="resumen-card promedio">
          <TrendingUp size={24} />
          <div>
            <span className="numero">{estadisticas.promedioCarga}</span>
            <span className="label">Promedio/Inst.</span>
          </div>
        </div>
      </div>

      {/* Distribución de Carga */}
      <div className="distribucion-panel">
        <h3><Zap size={18} /> Distribución de Carga</h3>
        <div className="distribucion-bars">
          <div className="dist-item">
            <div className="dist-label">
              <span className="dot libre"></span>
              Sin carga (0)
            </div>
            <div className="dist-bar">
              <div 
                className="dist-fill libre" 
                style={{ width: `${(estadisticas.sinCarga / estadisticas.totalInstituciones) * 100}%` }}
              ></div>
            </div>
            <span className="dist-count">{estadisticas.sinCarga}</span>
          </div>
          <div className="dist-item">
            <div className="dist-label">
              <span className="dot bajo"></span>
              Baja (1)
            </div>
            <div className="dist-bar">
              <div 
                className="dist-fill bajo" 
                style={{ width: `${(estadisticas.bajaCarga / estadisticas.totalInstituciones) * 100}%` }}
              ></div>
            </div>
            <span className="dist-count">{estadisticas.bajaCarga}</span>
          </div>
          <div className="dist-item">
            <div className="dist-label">
              <span className="dot medio"></span>
              Media (2-3)
            </div>
            <div className="dist-bar">
              <div 
                className="dist-fill medio" 
                style={{ width: `${(estadisticas.mediaCarga / estadisticas.totalInstituciones) * 100}%` }}
              ></div>
            </div>
            <span className="dist-count">{estadisticas.mediaCarga}</span>
          </div>
          <div className="dist-item">
            <div className="dist-label">
              <span className="dot alto"></span>
              Alta (4-5)
            </div>
            <div className="dist-bar">
              <div 
                className="dist-fill alto" 
                style={{ width: `${(estadisticas.altaCarga / estadisticas.totalInstituciones) * 100}%` }}
              ></div>
            </div>
            <span className="dist-count">{estadisticas.altaCarga}</span>
          </div>
          <div className="dist-item">
            <div className="dist-label">
              <span className="dot saturado"></span>
              Saturada (+5)
            </div>
            <div className="dist-bar">
              <div 
                className="dist-fill saturado" 
                style={{ width: `${(estadisticas.saturadas / estadisticas.totalInstituciones) * 100}%` }}
              ></div>
            </div>
            <span className="dist-count">{estadisticas.saturadas}</span>
          </div>
        </div>
      </div>

      <div className="paneles-grid">
        {/* Panel de Recomendaciones */}
        <div className="recomendaciones-panel">
          <h3>
            <ThumbsUp size={18} />
            Recomendadas para Asignación
            <span className="badge-count">{recomendadas.length}</span>
          </h3>
          <p className="panel-desc">Instituciones sin carga o con baja carga de casos</p>
          
          {recomendadas.length === 0 ? (
            <div className="empty-panel">
              <AlertCircle size={32} />
              <p>Todas las instituciones tienen carga de trabajo</p>
            </div>
          ) : (
            <div className="instituciones-lista">
              {recomendadas.map((inst, idx) => (
                <div key={inst.nombre} className={`institucion-card ${inst.nivelCarga}`}>
                  <div className="inst-rank">#{idx + 1}</div>
                  <div className="inst-info">
                    <span className="inst-nombre">{inst.nombre}</span>
                    <span className="inst-stats">
                      {inst.activos === 0 ? (
                        <span className="tag libre">Sin casos activos</span>
                      ) : (
                        <span className="tag bajo">{inst.activos} caso{inst.activos > 1 ? 's' : ''} activo{inst.activos > 1 ? 's' : ''}</span>
                      )}
                      {inst.completados > 0 && (
                        <span className="completados">{inst.completados} completado{inst.completados > 1 ? 's' : ''}</span>
                      )}
                    </span>
                  </div>
                  <div className="inst-badge" style={{ backgroundColor: getColorCarga(inst.nivelCarga) }}>
                    {getIconoRecomendacion(inst.recomendacion)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Panel de Alertas */}
        <div className="alertas-panel">
          <h3>
            <AlertTriangle size={18} />
            Alertas de Saturación
            <span className="badge-count alerta">{conAlertas.length}</span>
          </h3>
          <p className="panel-desc">Instituciones con alta carga que requieren atención</p>
          
          {conAlertas.length === 0 ? (
            <div className="empty-panel success">
              <CheckCircle size={32} />
              <p>No hay instituciones saturadas</p>
            </div>
          ) : (
            <div className="instituciones-lista">
              {conAlertas.map((inst, idx) => (
                <div key={inst.nombre} className={`institucion-card alerta ${inst.nivelCarga}`}>
                  <div className="inst-warning">
                    <AlertTriangle size={20} />
                  </div>
                  <div className="inst-info">
                    <span className="inst-nombre">{inst.nombre}</span>
                    <span className="inst-stats">
                      <span className="tag alto">{inst.activos} casos activos</span>
                      <span className="alerta-msg">{inst.alerta}</span>
                    </span>
                  </div>
                  <div className="carga-indicator">
                    <div className="carga-bar">
                      <div 
                        className="carga-fill"
                        style={{ 
                          width: `${Math.min(inst.activos * 10, 100)}%`,
                          backgroundColor: getColorCarga(inst.nivelCarga)
                        }}
                      ></div>
                    </div>
                    <span className="carga-label">{inst.activos}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Tabla Completa */}
      <div className="tabla-panel">
        <h3>
          <FileText size={18} />
          Análisis Completo por Institución
        </h3>
        <div className="tabla-wrapper">
          <table className="tabla-carga">
            <thead>
              <tr>
                <th>Institución</th>
                <th title="Casos activos pendientes">Activos</th>
                <th title="Casos completados/archivados">Completados</th>
                <th title="Total de casos">Total</th>
                <th title="Porcentaje de casos completados">% Completado</th>
                <th title="Nivel de carga de trabajo">Nivel Carga</th>
                <th title="Recomendación para asignación">Recomendación</th>
              </tr>
            </thead>
            <tbody>
              {analisisCarga.map(inst => (
                <tr key={inst.nombre} className={inst.alerta ? 'fila-alerta' : ''}>
                  <td className="nombre-cell">
                    {inst.nombre}
                    {inst.alerta && <AlertTriangle size={14} className="alerta-icon" />}
                  </td>
                  <td className="numero-cell">
                    <span className={`badge-numero ${inst.activos > 5 ? 'alto' : inst.activos > 0 ? 'normal' : 'cero'}`}>
                      {inst.activos}
                    </span>
                  </td>
                  <td className="numero-cell completado">{inst.completados}</td>
                  <td className="numero-cell">{inst.total}</td>
                  <td className="numero-cell">
                    <div className="progress-mini">
                      <div 
                        className="progress-fill"
                        style={{ width: `${inst.porcentajeCompletado}%` }}
                      ></div>
                      <span>{inst.porcentajeCompletado}%</span>
                    </div>
                  </td>
                  <td className="nivel-cell">
                    <span 
                      className={`nivel-badge ${inst.nivelCarga}`}
                      style={{ backgroundColor: getColorCarga(inst.nivelCarga) }}
                    >
                      {inst.nivelCarga.toUpperCase()}
                    </span>
                  </td>
                  <td className="recomendacion-cell">
                    <span className={`rec-badge ${inst.recomendacion}`}>
                      {getIconoRecomendacion(inst.recomendacion)}
                      {inst.recomendacion}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PanelCargaTrabajo;
